import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  createCandidate,
  deleteCandidate,
  getCandidates,
  getJobOpenings,
  updateCandidate,
} from "../api/recruitmentApi";

import { normalizeList, statusClass } from "./hrUtils";

import "./HRShared.css";
import "./Candidates.css";

const stages = [
  "Applied",
  "Screening",
  "Shortlisted",
  "Interview Scheduled",
  "Interview Completed",
  "Selected",
  "Offer Sent",
  "Offer Accepted",
  "Joined",
  "Rejected",
  "On Hold",
  "Interview Cancelled",
  "Candidate Not Interested",
];

const pipelineStages = [
  "Applied",
  "Screening",
  "Shortlisted",
  "Interview Scheduled",
  "Interview Completed",
  "Selected",
  "Offer Sent",
  "Offer Accepted",
  "Joined",
];

const noticePeriodOptions = [
  "Immediate",
  "15 Days",
  "1 Month",
  "2 Months",
  "3 Months",
  "More Than 3 Months",
];

const candidateSources = [
  "LinkedIn",
  "Indeed",
  "Naukri",
  "Employee Referral",
  "Company Website",
  "Consultancy",
  "Walk-in",
  "Other",
];

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const emptyForm = {
  candidateName: "",
  email: "",
  phone: "",
  jobOpening: "",
  experience: "",
  currentCompany: "",
  currentJobLocation: "",
  currentSalary: "",
  expectedSalary: "",
  noticePeriod: "",
  source: "LinkedIn",
  stage: "Applied",
  resumeUrl: "",
  interviewerName: "",
  interviewDate: "",
  interviewerRating: "",
  interviewerRemarks: "",
};

// Use the original candidate entry date, not interview/joining dates.
const getEntryDate = (candidate) => {
  const value = candidate.createdAt || candidate.applicationDate;
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatSalary = (value) => {
  const amount = Number(value || 0);
  if (!amount) return "-";
  return `₹${amount.toLocaleString("en-IN")}`;
};

const Candidates = () => {
  const tableScrollRef = useRef(null);
  const topScrollRef = useRef(null);
  const scrollLockRef = useRef(false);

  const [tableWidth, setTableWidth] = useState(0);
  const [allItems, setAllItems] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [openings, setOpenings] = useState([]);
  const [filters, setFilters] = useState({ search: "", stage: "" });
  const [form, setForm] = useState(emptyForm);
  const [resumeFile, setResumeFile] = useState(null);
  const [remarksCandidate, setRemarksCandidate] = useState(null);
  const [editingId, setEditingId] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [view, setView] = useState("table");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [error, setError] = useState("");

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");

      const [candidateResponse, openingResponse] = await Promise.all([
        getCandidates(filters),
        getJobOpenings({ status: "Open", limit: 500 }),
      ]);

      setAllItems(normalizeList(candidateResponse));
      setOpenings(normalizeList(openingResponse));
    } catch (requestError) {
      setError(
        requestError?.message || "Unable to load candidate information.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Debounced reload when search / stage change.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadData();
    }, 300);

    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.stage]);

  // Years that actually have records.
  const availableYears = useMemo(
    () =>
      [
        ...new Set(
          allItems
            .map((candidate) => getEntryDate(candidate)?.getFullYear())
            .filter(Boolean),
        ),
      ].sort((a, b) => b - a),
    [allItems],
  );

  // Client-side year / month filter.
  // NOTE: `items` must be declared BEFORE any hook that lists it as a dependency.
  const items = useMemo(
    () =>
      allItems.filter((candidate) => {
        const date = getEntryDate(candidate);

        if (
          selectedYear &&
          (!date || date.getFullYear() !== Number(selectedYear))
        ) {
          return false;
        }

        if (
          selectedMonth &&
          (!date || date.getMonth() + 1 !== Number(selectedMonth))
        ) {
          return false;
        }

        return true;
      }),
    [allItems, selectedYear, selectedMonth],
  );

  const groupedCandidates = useMemo(
    () =>
      pipelineStages.map((stage) => ({
        stage,
        candidates: items.filter((item) => item.stage === stage),
      })),
    [items],
  );

  // Keep the top scrollbar sized to the actual table width.
  useEffect(() => {
    if (loading || view !== "table") return undefined;

    const wrapper = tableScrollRef.current;
    if (!wrapper) return undefined;

    const measure = () => setTableWidth(wrapper.scrollWidth);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(wrapper);

    const table = wrapper.querySelector("table");
    if (table) observer.observe(table);

    return () => observer.disconnect();
  }, [loading, view, items]);

  // Two-way scroll sync without a feedback loop (this was causing the jitter).
  const syncScroll = (sourceEl, targetRef) => {
    if (scrollLockRef.current) {
      scrollLockRef.current = false;
      return;
    }

    const target = targetRef.current;
    if (!target || target.scrollLeft === sourceEl.scrollLeft) return;

    scrollLockRef.current = true;
    target.scrollLeft = sourceEl.scrollLeft;
  };

  const updateFormField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const openCreate = () => {
    setEditingId("");
    setResumeFile(null);
    setForm({ ...emptyForm });
    setError("");
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item._id);
    setResumeFile(null);
    setForm({
      candidateName: item.candidateName || "",
      email: item.email || "",
      phone: item.phone || "",
      jobOpening: item.jobOpening?._id || item.jobOpening || "",
      experience: item.experience ?? "",
      currentCompany: item.currentCompany || "",
      currentJobLocation: item.currentJobLocation || item.currentLocation || "",
      currentSalary: item.currentSalary ?? "",
      expectedSalary: item.expectedSalary ?? "",
      noticePeriod: item.noticePeriod || "",
      source: item.source || "LinkedIn",
      stage: item.stage || "Applied",
      resumeUrl: item.resumeUrl || "",
      interviewerName: item.interviewerName || "",
      interviewDate: item.interviewDate
        ? String(item.interviewDate).slice(0, 10)
        : "",
      interviewerRating: item.interviewerRating ?? "",
      interviewerRemarks: item.interviewerRemarks || "",
    });
    setError("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditingId("");
    setResumeFile(null);
    setForm({ ...emptyForm });
  };

  const openResume = async (item, download = false) => {
    try {
      setError("");

      const token =
        localStorage.getItem("token") ||
        localStorage.getItem("authToken") ||
        sessionStorage.getItem("token");

      const base =
        process.env.REACT_APP_API_BASE_URL ||
        (["localhost", "127.0.0.1"].includes(window.location.hostname)
          ? "http://localhost:5003"
          : "https://erp-api.sdcllp.co.in");

      const response = await fetch(
        `${base}/api/hr/candidates/${encodeURIComponent(item._id)}/resume`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );

      if (!response.ok) {
        throw new Error(
          "Resume could not be opened. Check resume route and uploaded file.",
        );
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const extension = (item.resumeUrl || "").split(".").pop().toLowerCase();
      const safeExtension = ["pdf", "doc", "docx"].includes(extension)
        ? extension
        : "pdf";
      const filename = `${(item.candidateName || "Candidate").replace(/[^a-z0-9_-]/gi, "_")}_Resume.${safeExtension}`;

      if (download) {
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        const newTab = window.open(blobUrl, "_blank");

        if (!newTab) {
          const a = document.createElement("a");
          a.href = blobUrl;
          a.target = "_blank";
          a.rel = "noopener noreferrer";
          a.click();
        }
      }

      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      setError(err.message || "Unable to open resume.");
    }
  };

  const saveCandidate = async (event) => {
    event.preventDefault();

    const cleanName = String(form.candidateName || "").trim();
    const cleanEmail = String(form.email || "")
      .trim()
      .toLowerCase();
    const cleanPhone = String(form.phone || "").trim();

    if (!cleanName) {
      setError("Candidate name is required.");
      return;
    }

    if (!cleanEmail) {
      setError("Candidate email is required.");
      return;
    }

    if (!form.jobOpening) {
      setError("Please select a job opening.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      const payload = {
        candidateName: cleanName,
        email: cleanEmail,
        phone: cleanPhone,
        jobOpening: form.jobOpening,
        experience: form.experience === "" ? 0 : Number(form.experience),
        currentCompany: String(form.currentCompany || "").trim(),
        currentJobLocation: String(form.currentJobLocation || "").trim(),
        currentSalary:
          form.currentSalary === "" ? 0 : Number(form.currentSalary),
        expectedSalary:
          form.expectedSalary === "" ? 0 : Number(form.expectedSalary),
        noticePeriod: form.noticePeriod || "",
        source: form.source || "Other",
        stage: form.stage || "Applied",
        interviewerName: form.interviewerName.trim(),
        interviewDate: form.interviewDate || null,
        interviewerRating:
          form.interviewerRating === "" ? null : Number(form.interviewerRating),
        interviewerRemarks: form.interviewerRemarks.trim(),
      };

      const requestData = new FormData();

      Object.entries(payload).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          requestData.append(key, String(value));
        }
      });

      if (resumeFile) requestData.append("resume", resumeFile);

      if (editingId) {
        await updateCandidate(editingId, requestData);
      } else {
        await createCandidate(requestData);
      }

      setModalOpen(false);
      setResumeFile(null);
      setEditingId("");
      setForm({ ...emptyForm });

      await loadData();
    } catch (requestError) {
      setError(requestError?.message || "Unable to save candidate.");
    } finally {
      setSaving(false);
    }
  };

  const removeCandidate = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this candidate?",
    );

    if (!confirmed) return;

    try {
      setDeletingId(id);
      setError("");
      await deleteCandidate(id);
      await loadData();
    } catch (requestError) {
      setError(requestError?.message || "Unable to delete candidate.");
    } finally {
      setDeletingId("");
    }
  };

  return (
    <div className="hr-page hr-candidates-page">
      <header className="hr-page-header">
        <div>
          <h1>Candidates</h1>
          <p>
            Track applicants from application through selection and joining.
          </p>
        </div>

        <div className="hr-header-actions">
          <button
            type="button"
            className="hr-btn hr-btn-secondary"
            onClick={() =>
              setView((current) => (current === "table" ? "pipeline" : "table"))
            }
          >
            {view === "table" ? "Pipeline View" : "Table View"}
          </button>

          <button
            type="button"
            className="hr-btn hr-btn-primary"
            onClick={openCreate}
          >
            + Add Candidate
          </button>
        </div>
      </header>

      {error && <div className="hr-error">{error}</div>}

      <section className="hr-card hr-panel hr-candidate-filter-panel">
        <div className="hr-filter-row">
          <input
            className="hr-input hr-candidate-search"
            type="search"
            placeholder="Search candidate, email or company..."
            value={filters.search}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                search: event.target.value,
              }))
            }
          />

          <select
            className="hr-select hr-candidate-stage"
            value={filters.stage}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                stage: event.target.value,
              }))
            }
          >
            <option value="">All stages</option>
            {stages.map((stage) => (
              <option value={stage} key={stage}>
                {stage}
              </option>
            ))}
          </select>

          <select
            className="hr-select hr-candidate-year"
            value={selectedYear}
            onChange={(event) => setSelectedYear(event.target.value)}
            aria-label="Filter by entry year"
          >
            <option value="">All Years</option>
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>

          <select
            className="hr-select hr-candidate-month"
            value={selectedMonth}
            onChange={(event) => setSelectedMonth(event.target.value)}
            aria-label="Filter by entry month"
          >
            <option value="">All Months</option>
            {monthNames.map((month, index) => (
              <option key={month} value={index + 1}>
                {month}
              </option>
            ))}
          </select>
        </div>
      </section>

      {loading ? (
        <div className="hr-card hr-loading">Loading candidates...</div>
      ) : view === "pipeline" ? (
        <section className="hr-candidate-kanban">
          {groupedCandidates.map((group) => (
            <div className="hr-candidate-kanban-column" key={group.stage}>
              <div className="hr-candidate-kanban-head">
                <h3>{group.stage}</h3>
                <span>{group.candidates.length}</span>
              </div>

              <div className="hr-candidate-kanban-body">
                {group.candidates.map((item) => (
                  <article
                    className="hr-candidate-card"
                    key={item._id}
                    onClick={() => openEdit(item)}
                  >
                    <div className="hr-candidate-card-head">
                      <strong>{item.candidateName}</strong>
                      <span className={`hr-status ${statusClass(item.stage)}`}>
                        {item.stage}
                      </span>
                    </div>

                    <small>
                      {item.jobOpening?.jobTitle ||
                        item.position ||
                        "No position selected"}
                    </small>

                    <div className="hr-candidate-card-details">
                      <span>
                        <b>Company:</b> {item.currentCompany || "-"}
                      </span>
                      <span>
                        <b>Location:</b>{" "}
                        {item.currentJobLocation || item.currentLocation || "-"}
                      </span>
                      <span>
                        <b>Experience:</b>{" "}
                        {item.experience ? `${item.experience} years` : "-"}
                      </span>
                      <span>
                        <b>Notice:</b> {item.noticePeriod || "-"}
                      </span>
                    </div>

                    <div className="hr-candidate-card-contact">
                      <span>{item.email || "-"}</span>
                      <span>{item.phone || "-"}</span>
                    </div>
                  </article>
                ))}

                {!group.candidates.length && (
                  <div className="hr-candidate-column-empty">No candidates</div>
                )}
              </div>
            </div>
          ))}
        </section>
      ) : (
        <div className="hr-candidate-table-container">
          <div
            ref={topScrollRef}
            className="hr-candidate-top-scroll"
            aria-label="Scroll candidates table horizontally"
            onScroll={(event) =>
              syncScroll(event.currentTarget, tableScrollRef)
            }
          >
            <div style={{ width: tableWidth, height: 1 }} />
          </div>

          <div
            ref={tableScrollRef}
            className="hr-table-wrap hr-candidate-table-wrap"
            onScroll={(event) => syncScroll(event.currentTarget, topScrollRef)}
          >
            <table className="hr-table hr-candidate-table">
              <thead>
                <tr>
                  <th>Candidate</th>
                  <th>Position</th>
                  <th>Experience</th>
                  <th>Current Job</th>
                  <th>Salary</th>
                  <th>Notice Period</th>
                  <th>Source</th>
                  <th>Stage</th>
                  <th>Contact</th>
                  <th>Resume</th>
                  <th>Interviewer</th>
                  <th>Rating</th>
                  <th>Remarks</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {items.length ? (
                  items.map((item) => (
                    <tr key={item._id}>
                      <td>
                        <div className="hr-candidate-name-cell">
                          <span className="hr-candidate-avatar">
                            {String(item.candidateName || "C")
                              .trim()
                              .charAt(0)
                              .toUpperCase()}
                          </span>
                          <div>
                            <strong>{item.candidateName || "-"}</strong>
                            <small>{item.currentCompany || "No company"}</small>
                          </div>
                        </div>
                      </td>

                      <td>
                        <div className="hr-candidate-position">
                          <strong>
                            {item.jobOpening?.jobTitle || item.position || "-"}
                          </strong>
                          <small>
                            {item.jobOpening?.department ||
                              item.department ||
                              ""}
                          </small>
                        </div>
                      </td>

                      <td>
                        {item.experience ? `${item.experience} years` : "-"}
                      </td>

                      <td>
                        <div className="hr-candidate-current-job">
                          <strong>
                            {item.currentJobLocation ||
                              item.currentLocation ||
                              "-"}
                          </strong>
                          <small>{item.currentCompany || "-"}</small>
                        </div>
                      </td>

                      <td>
                        <div className="hr-candidate-salary">
                          <div className="hr-candidate-salary-row">
                            <small>Current</small>
                            <strong>{formatSalary(item.currentSalary)}</strong>
                          </div>
                          <div className="hr-candidate-salary-row">
                            <small>Expected</small>
                            <strong>{formatSalary(item.expectedSalary)}</strong>
                          </div>
                        </div>
                      </td>

                      <td>
                        <span className="hr-notice-badge">
                          {item.noticePeriod || "-"}
                        </span>
                      </td>

                      <td>{item.source || "-"}</td>

                      <td>
                        <span
                          className={`hr-status ${statusClass(item.stage)}`}
                        >
                          {item.stage || "Applied"}
                        </span>
                      </td>

                      <td>
                        <div className="hr-candidate-contact">
                          <span>{item.email || "-"}</span>
                          <small>{item.phone || "-"}</small>
                        </div>
                      </td>

                      <td>
                        {item.resumeUrl ? (
                          <div className="hr-inline-actions">
                            <button
                              type="button"
                              className="hr-btn hr-btn-secondary"
                              onClick={() => openResume(item)}
                            >
                              View
                            </button>
                            <button
                              type="button"
                              className="hr-btn hr-btn-secondary"
                              onClick={() => openResume(item, true)}
                            >
                              Download
                            </button>
                          </div>
                        ) : (
                          <span className="hr-muted-text">Not Uploaded</span>
                        )}
                      </td>

                      <td>
                        <div className="hr-candidate-interviewer">
                          <strong>{item.interviewerName || "-"}</strong>
                          <small>
                            {item.interviewDate
                              ? new Date(item.interviewDate).toLocaleDateString(
                                  "en-IN",
                                  {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  },
                                )
                              : "No interview date"}
                          </small>
                        </div>
                      </td>

                      <td className="hr-candidate-rating">
                        {item.interviewerRating != null &&
                        item.interviewerRating !== ""
                          ? `${item.interviewerRating} / 5`
                          : "-"}
                      </td>

                      <td>
                        {item.interviewerRemarks ? (
                          <div className="hr-candidate-remarks">
                            <span title={item.interviewerRemarks}>
                              {item.interviewerRemarks}
                            </span>
                            <button
                              type="button"
                              className="hr-btn hr-btn-secondary"
                              onClick={() => setRemarksCandidate(item)}
                            >
                              View Remarks
                            </button>
                          </div>
                        ) : (
                          <span className="hr-muted-text">No Remarks</span>
                        )}
                      </td>

                      <td>
                        <div className="hr-inline-actions">
                          <button
                            type="button"
                            className="hr-btn hr-btn-secondary"
                            onClick={() => openEdit(item)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="hr-btn hr-btn-danger"
                            disabled={deletingId === item._id}
                            onClick={() => removeCandidate(item._id)}
                          >
                            {deletingId === item._id ? "Deleting..." : "Delete"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="14" className="hr-empty">
                      No candidates found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {remarksCandidate && (
        <div
          className="hr-modal-backdrop"
          onMouseDown={() => setRemarksCandidate(null)}
        >
          <div
            className="hr-modal hr-candidate-modal hr-remarks-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Interviewer Remarks"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="hr-modal-head">
              <div>
                <h2>Interviewer Remarks</h2>
                <p>{remarksCandidate.candidateName || "Candidate"}</p>
              </div>

              <button
                type="button"
                className="hr-modal-close"
                onClick={() => setRemarksCandidate(null)}
                aria-label="Close remarks"
              >
                ×
              </button>
            </div>

            <div className="hr-modal-body">
              <div className="hr-form-grid hr-remarks-meta">
                <div className="hr-field">
                  <strong>Interviewer</strong>
                  <div>{remarksCandidate.interviewerName || "-"}</div>
                </div>

                <div className="hr-field">
                  <strong>Interview Date</strong>
                  <div>
                    {remarksCandidate.interviewDate
                      ? new Date(
                          remarksCandidate.interviewDate,
                        ).toLocaleDateString("en-IN")
                      : "-"}
                  </div>
                </div>

                <div className="hr-field">
                  <strong>Rating</strong>
                  <div>
                    {remarksCandidate.interviewerRating != null &&
                    remarksCandidate.interviewerRating !== ""
                      ? `${remarksCandidate.interviewerRating} / 5`
                      : "-"}
                  </div>
                </div>
              </div>

              <div className="hr-field hr-remarks-text">
                <strong>Remarks</strong>
                <p>
                  {remarksCandidate.interviewerRemarks ||
                    "No remarks available."}
                </p>
              </div>
            </div>

            <div className="hr-modal-foot">
              <button
                type="button"
                className="hr-btn hr-btn-secondary"
                onClick={() => setRemarksCandidate(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="hr-modal-backdrop" onMouseDown={closeModal}>
          <form
            className="hr-modal hr-candidate-modal"
            onMouseDown={(event) => event.stopPropagation()}
            onSubmit={saveCandidate}
          >
            <div className="hr-modal-head">
              <div>
                <h2>{editingId ? "Edit Candidate" : "Add Candidate"}</h2>
                <p>
                  Add the candidate’s current job, salary and recruitment
                  information.
                </p>
              </div>

              <button
                type="button"
                className="hr-modal-close"
                onClick={closeModal}
                disabled={saving}
                aria-label="Close candidate form"
              >
                ×
              </button>
            </div>

            <div className="hr-modal-body">
              <div className="hr-form-section">
                <h3>Candidate Information</h3>

                <div className="hr-form-grid">
                  <div className="hr-field">
                    <label>Candidate Name *</label>
                    <input
                      className="hr-input"
                      type="text"
                      required
                      placeholder="Enter candidate name"
                      value={form.candidateName}
                      onChange={(event) =>
                        updateFormField("candidateName", event.target.value)
                      }
                    />
                  </div>

                  <div className="hr-field">
                    <label>Email *</label>
                    <input
                      className="hr-input"
                      type="email"
                      required
                      placeholder="Enter email address"
                      value={form.email}
                      onChange={(event) =>
                        updateFormField("email", event.target.value)
                      }
                    />
                  </div>

                  <div className="hr-field">
                    <label>Phone</label>
                    <input
                      className="hr-input"
                      type="text"
                      placeholder="Enter phone number"
                      value={form.phone}
                      onChange={(event) =>
                        updateFormField("phone", event.target.value)
                      }
                    />
                  </div>

                  <div className="hr-field">
                    <label>Experience (Years)</label>
                    <input
                      className="hr-input"
                      type="number"
                      min="0"
                      step="0.1"
                      placeholder="Example: 3"
                      value={form.experience}
                      onChange={(event) =>
                        updateFormField("experience", event.target.value)
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="hr-form-section">
                <h3>Current Employment</h3>

                <div className="hr-form-grid">
                  <div className="hr-field">
                    <label>Current Company</label>
                    <input
                      className="hr-input"
                      type="text"
                      placeholder="Enter current company"
                      value={form.currentCompany}
                      onChange={(event) =>
                        updateFormField("currentCompany", event.target.value)
                      }
                    />
                  </div>

                  <div className="hr-field">
                    <label>Current Job Location</label>
                    <input
                      className="hr-input"
                      type="text"
                      placeholder="Example: Ahmedabad"
                      value={form.currentJobLocation}
                      onChange={(event) =>
                        updateFormField(
                          "currentJobLocation",
                          event.target.value,
                        )
                      }
                    />
                  </div>

                  <div className="hr-field">
                    <label>Current Salary</label>
                    <input
                      className="hr-input"
                      type="number"
                      min="0"
                      placeholder="Enter current salary"
                      value={form.currentSalary}
                      onChange={(event) =>
                        updateFormField("currentSalary", event.target.value)
                      }
                    />
                  </div>

                  <div className="hr-field">
                    <label>Expected Salary</label>
                    <input
                      className="hr-input"
                      type="number"
                      min="0"
                      placeholder="Enter expected salary"
                      value={form.expectedSalary}
                      onChange={(event) =>
                        updateFormField("expectedSalary", event.target.value)
                      }
                    />
                  </div>

                  <div className="hr-field">
                    <label>Notice Period</label>
                    <select
                      className="hr-select"
                      value={form.noticePeriod}
                      onChange={(event) =>
                        updateFormField("noticePeriod", event.target.value)
                      }
                    >
                      <option value="">Select notice period</option>
                      {noticePeriodOptions.map((value) => (
                        <option value={value} key={value}>
                          {value}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="hr-form-section">
                <h3>Hiring Information</h3>

                <div className="hr-form-grid">
                  <div className="hr-field">
                    <label>Job Opening *</label>
                    <select
                      className="hr-select"
                      required
                      value={form.jobOpening}
                      onChange={(event) =>
                        updateFormField("jobOpening", event.target.value)
                      }
                    >
                      <option value="">Select position</option>
                      {openings.map((opening) => (
                        <option value={opening._id} key={opening._id}>
                          {opening.jobTitle}
                          {opening.department ? ` — ${opening.department}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="hr-field">
                    <label>Source</label>
                    <select
                      className="hr-select"
                      value={form.source}
                      onChange={(event) =>
                        updateFormField("source", event.target.value)
                      }
                    >
                      {candidateSources.map((value) => (
                        <option value={value} key={value}>
                          {value}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="hr-field">
                    <label>Stage</label>
                    <select
                      className="hr-select"
                      value={form.stage}
                      onChange={(event) =>
                        updateFormField("stage", event.target.value)
                      }
                    >
                      {stages.map((value) => (
                        <option value={value} key={value}>
                          {value}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="hr-form-section">
                <h3>Resume</h3>

                <div className="hr-field">
                  <label>Upload Resume (PDF, DOC, DOCX — max 5 MB)</label>
                  <input
                    className="hr-input"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(event) => {
                      const file = event.target.files?.[0];

                      if (!file) {
                        setResumeFile(null);
                        return;
                      }

                      const valid = /\.(pdf|doc|docx)$/i.test(file.name);

                      if (!valid || file.size > 5 * 1024 * 1024) {
                        setError(
                          "Select a PDF, DOC or DOCX resume under 5 MB.",
                        );
                        event.target.value = "";
                        setResumeFile(null);
                        return;
                      }

                      setError("");
                      setResumeFile(file);
                    }}
                  />

                  {resumeFile && <small>Selected: {resumeFile.name}</small>}

                  {form.resumeUrl && editingId && (
                    <button
                      type="button"
                      className="hr-btn hr-btn-secondary hr-existing-resume-btn"
                      onClick={() =>
                        openResume({
                          _id: editingId,
                          candidateName: form.candidateName,
                          resumeUrl: form.resumeUrl,
                        })
                      }
                    >
                      View Existing Resume
                    </button>
                  )}
                </div>
              </div>

              <div className="hr-form-section">
                <h3>Interviewer Feedback</h3>

                <div className="hr-form-grid">
                  <div className="hr-field">
                    <label>Interviewer Name</label>
                    <input
                      className="hr-input"
                      value={form.interviewerName}
                      onChange={(e) =>
                        updateFormField("interviewerName", e.target.value)
                      }
                      placeholder="Interviewer name"
                    />
                  </div>

                  <div className="hr-field">
                    <label>Interview Date</label>
                    <input
                      className="hr-input"
                      type="date"
                      value={form.interviewDate}
                      onChange={(e) =>
                        updateFormField("interviewDate", e.target.value)
                      }
                    />
                  </div>

                  <div className="hr-field">
                    <label>Rating</label>
                    <select
                      className="hr-select"
                      value={form.interviewerRating}
                      onChange={(e) =>
                        updateFormField("interviewerRating", e.target.value)
                      }
                    >
                      <option value="">Select rating</option>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <option key={n} value={n}>
                          {n} / 5
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="hr-field hr-field-full">
                    <label>Interviewer Remarks</label>
                    <textarea
                      className="hr-input"
                      rows={4}
                      value={form.interviewerRemarks}
                      onChange={(e) =>
                        updateFormField("interviewerRemarks", e.target.value)
                      }
                      placeholder="Write interview feedback here..."
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="hr-modal-foot">
              <button
                type="button"
                className="hr-btn hr-btn-secondary"
                onClick={closeModal}
                disabled={saving}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="hr-btn hr-btn-primary"
                disabled={saving}
              >
                {saving
                  ? "Saving..."
                  : editingId
                    ? "Update Candidate"
                    : "Save Candidate"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Candidates;
